import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useActivityFeed } from '../context/ActivityFeedContext';

const ActivityFeedContainer = styled.div`
  width: 100%;
  margin-top: 20px;
`;



const SeeAllButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: white;
  }
`;

const ActivityList = styled.div`
  display: grid;
  gap: 16px;
`;

const ActivityItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: ${props => props.hoverColor || '#17A2B8'};
    background: ${props => props.hoverColor ? `${props.hoverColor}0D` : 'rgba(23, 162, 184, 0.05)'};
  }
`;

const ActivityContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ActivityTitle = styled.div`
  font-size: 12px;
  color: white;
  font-weight: 600;
`;

const ActivityDescription = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const ActivityTime = styled.div`
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
`;

const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  flex-wrap: wrap;
`;

const MetaTag = styled.span`
  background: ${props => props.color ? `${props.color}33` : 'rgba(23, 162, 184, 0.2)'};
  color: ${props => props.color || 'rgba(23, 162, 184, 1)'};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
`;

const SimpleEmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
  color: rgba(255, 255, 255, 0.7);
`;

const PlusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &::before {
    content: '+';
    font-size: 24px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 300;
  }
`;

const EmptyStateText = styled.div`
  flex: 1;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: left;
  padding: 60px 0;
`;

const EmptyStateTitle = styled.h3`
  color: white;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateDescription = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 300px;
`;

const EmptyStateButton = styled.button`
  background: #17A2B8;
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 12px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #20B2AA;
  }
`;

const ActivityFeed = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const { activities, isLoading: loading } = useActivityFeed();

  const getEmptyStateContent = () => {
    return {
      title: 'No recent activity',
      description: 'Start using your apps to see activity here',
      buttonText: 'Get Started',
      action: () => navigate('/main')
    };
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h`;
    } else {
      return `${Math.floor(diffInSeconds / 86400)}d`;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      bookings: '#FFFF00',
      catalog: '#17A2B8',
      merch: '#9932CC',
      social: '#FF6B6B',
      playlist: '#34C759',
      charting: '#FF9500',
      playlisting: '#5856D6',
      'social-listening': '#007AFF',
      'spotify-revenue': '#1DB954',
      reminders: '#FF3B30'
    };
    return colors[type] || '#8E8E93';
  };

  const renderActivityItem = (activity) => {
    const activityColor = getTypeColor(activity.type);
    
    return (
      <ActivityItem key={activity.id} hoverColor={activityColor}>
        <ActivityContent>
          <ActivityHeader>
            <ActivityTitle>{activity.title}</ActivityTitle>
            <ActivityTime>{formatTimeAgo(activity.timestamp)}</ActivityTime>
          </ActivityHeader>
          <ActivityDescription>{activity.description}</ActivityDescription>
          {activity.meta && (
            <ActivityMeta>
              <MetaTag color={getTypeColor(activity.type)}>{activity.type}</MetaTag>
              {activity.meta}
            </ActivityMeta>
          )}
        </ActivityContent>
      </ActivityItem>
    );
  };

  // Show only first 4 activities on the main page
  const displayActivities = Array.isArray(activities) ? activities.slice(0, 4) : [];

  if (loading) {
    return (
      <ActivityFeedContainer>
        {!isCollapsed && (
          <EmptyState>
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.5)', 
              fontSize: '12px', 
              fontWeight: '600',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center'
            }}>
              Loading activity feed
              <span className="loading-dots" style={{
                marginLeft: '2px'
              }}></span>
          </div>
          <style>{`
              @keyframes thinking {
                0% { content: ''; }
                33% { content: '.'; }
                66% { content: '..'; }
                100% { content: '...'; }
              }
              .loading-dots::after {
                content: '';
                animation: thinking 1.2s infinite;
            }
          `}</style>
          </EmptyState>
        )}
      </ActivityFeedContainer>
    );
  }

  return (
    <ActivityFeedContainer>
      {displayActivities.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>{getEmptyStateContent().title}</EmptyStateTitle>
          <EmptyStateDescription>{getEmptyStateContent().description}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <ActivityList>
          {displayActivities.map(renderActivityItem)}
        </ActivityList>
      )}
    </ActivityFeedContainer>
  );
};

export default ActivityFeed; 